<template>
  <div class="flex flex-col relative">
    <div class="w-80">
      <div
        v-if="isLoadingSymbol"
        class="flex items-center justify-center h-[470px] w-[320px]"
      >
        <NLoading class="text-brand-primary" />
      </div>

      <NTabs
        v-else
        v-model="tabs"
        class="w-full mb-1"
        content-class="dialog-max-h:max-h-[20rem]"
      >
        <OrderFormTab
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'orderFormDialog',
            component: 'buyTabButton',
          }"
          v-bind="props"
          :model="baseModel"
          :symbol="symbol"
          :is-disabled="isEditOrderForm && type !== 'buy'"
          :is-edit-order="isEditOrderForm"
          :portfolio-quantity="portfolioQuantity"
          :is-order-status-pending="
            selectedOrder && isPendingOrder(selectedOrder)
          "
          type="buy"
          :title="t('common.buy')"
          color="green"
          :is-under-writing="isUnderWriting"
          :bonus-share="portfolioBonusShare"
        />

        <OrderFormTab
          v-gid="{
            module: 'order',
            screen: 'home',
            section: 'orderFormDialog',
            component: 'sellTabButton',
          }"
          v-bind="props"
          :model="baseModel"
          :symbol="symbol"
          :is-edit-order="isEditOrderForm"
          :is-disabled="(isEditOrderForm || isUnderWriting) && type !== 'sell'"
          :portfolio-quantity="portfolioQuantity"
          :is-order-status-pending="
            selectedOrder && isPendingOrder(selectedOrder)
          "
          type="sell"
          :title="t('common.sell')"
          color="red"
          :is-under-writing="isUnderWriting"
          :bonus-share="portfolioBonusShare"
        />
      </NTabs>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useLivePortfolioQuery } from '@core/portfolio';
import { isPendingOrder, ORDER_SIDES_TABS } from '@core/order';
import { OrderItem } from '@libs/api-reference';
import { useI18n } from 'vue-i18n';

import { useLiveSymbolQuery } from '@/modules/symbol';
import { OrderFormTab } from '@/modules/order';

type OrderFormDialogEmitsType = {
  (e?: 'tabChange', value?: string): void;
};

type OrderFormDialogPropsType = {
  baseModel: {
    quantity?: null | number;
    price?: null | number;
  };
  isin: string;
  type: 'buy' | 'sell';
  isEditOrderForm?: boolean;
  selectedOrder?: OrderItem;
  orderId?: string;
  isLinkToPrice?: boolean;
  dialogId?: string;
  isUnderWriting?: boolean;
};

const emits = defineEmits<OrderFormDialogEmitsType>();
const props = defineProps<OrderFormDialogPropsType>();

const { t } = useI18n();

const tabs = ref(ORDER_SIDES_TABS[props.type]);
const symbolIsin = ref(props.isin);
const type = ref(props.type);

const { symbol, isLoadingSymbol } = useLiveSymbolQuery(symbolIsin);

const { symbols } = useLivePortfolioQuery({
  listen: false,
  unsubscribe: false,
});

const portfolioQuantity = computed(
  () =>
    symbols.value.find(
      (portfolioSymbol) =>
        portfolioSymbol.instrumentIsin?.substring(0, 11) ===
        symbol.value.isin?.substring(0, 11)
    )?.quantity ?? 0
);

const portfolioBonusShare = computed(
  () =>
    symbols.value.find(
      (portfolioSymbol) => portfolioSymbol.instrumentIsin === symbol.value.isin
    )?.bonusShare
);

watch(tabs, () => {
  if (tabs.value === ORDER_SIDES_TABS.buy) {
    type.value = 'buy';
  } else {
    type.value = 'sell';
  }

  emits('tabChange', type.value);
});
</script>
